import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFocusable, FocusContext, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { MenuRow } from './MenuRow/MenuRow';
import { PastOrdersRow } from './PastOrders/PastOrderRow/PastOrdersRow';
import { AppDispatch, useSelector } from '../../redux/store';
import { useDispatch } from 'react-redux';
import { setAppBackground, setAppBackgroundTwClass } from '../../redux/slices/layoutSlice';
import { Header } from '../../components/Header';
import createTranslateFunction from '../../i18n/createTranslateFunction';
import { Bounce, toast } from 'react-toastify';

type HomeScreenProps = {
    focusKey: string;
};

export const HomeScreen = ({ focusKey: focusKeyParam }: HomeScreenProps) => {
    const TranslateFunction = createTranslateFunction('home_screen');
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const userAuth = useSelector((state) => state.userAuth);
    const userCart = useSelector((state) => state.cart);
    const navigate = useNavigate();

    const [showExitConfirmation, setShowExitConfirmation] = useState(false);

    const { ref, focusKey, focusSelf } = useFocusable({
        focusKey: focusKeyParam,
        preferredChildFocusKey: 'MENU-ROW',
    });

    const onRowFocus = useCallback(
        ({ y }) => {
            if (ref.current) {
                ref.current.scrollTop = y;
                ref.current.style.scrollBehavior = 'smooth';
            }
        },
        [ref],
    );

    useEffect(() => {
        focusSelf();
        dispatch(
            setAppBackground([
                'https://pizzahut-assets.s3.amazonaws.com/screen-bg/menu/faded3.jpg',
                'bg-no-repeat bg-cover bg-center',
            ]),
        );
        if (!userAuth.user?.auth) {
            navigate('/welcome', { replace: true });
            return;
        }

        // SIMPLIFIED History Reset:
        // This ensures that when HomeScreen is active, react-router's history
        // effectively treats '/' as the current and only viable entry.
        // If the app is already at '/', it replaces the current entry with itself.
        // If navigating from another route (e.g., /welcome), it replaces /welcome with /.
        navigate('/', { replace: true });

        // REMOVE the setTimeout block with direct window.history manipulation:
        // const timer = setTimeout(() => {
        //     if (window.history.state !== null || window.location.pathname !== '/') {
        //         window.history.pushState(null, '', '/');
        //         window.history.replaceState({}, '', '/');
        //     }
        // }, 100);
        // return () => clearTimeout(timer);
    }, [focusSelf, dispatch, navigate, userAuth.user?.auth]); // Dependencies remain the same

    // const handleExitConfirm = useCallback(() => {
    //     if (window.webOS && window.webOS.platformBack) {
    //         window.webOS.platformBack();
    //     } else {
    //         window.close(); // Fallback
    //     }
    //     setShowExitConfirmation(false); // Technically, app might close before this runs
    // }, []);

    const showBackButtonToast = (message: string) => {
        toast.error(message, {
            position: 'top-center',
            theme: 'colored',
            transition: Bounce,
        });
    };

    const handleBackButton = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Back' || event.keyCode === 461 || event.keyCode === 10009) {
                // webOS Back (461), Common 'Back' key, Samsung Tizen (10009)
                if (location.pathname === '/') {
                    // Only on HomeScreen
                    event.preventDefault();
                    event.stopPropagation();
                    navigate('/', { replace: true });
                    return false;
                }
                // For other screens, or if not on home screen, let default behavior (e.g., react-router) handle it.
            }
        },
        [location.pathname, showExitConfirmation],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleBackButton, true); // Use capture phase
        return () => {
            document.removeEventListener('keydown', handleBackButton, true);
        };
    }, [handleBackButton]); // Re-bind if handleBackButton changes

    return (
        <FocusContext.Provider value={focusKey}>
            <div className={`h-full w-full flex flex-col justify-between pr-[2.5vw] pb-0`}>
                <Header />
                <div className="w-[100%] h-[20vw] flex flex-col justify-center mt-[3vw] mb-[3vw]">
                    <span className="text-[4.6vw] font-bold mb-[1vw]">{`${TranslateFunction(`title`, { name: userAuth.user?.first_name })}`}</span>
                    {userCart.cart.length > 0 && (
                        <span className="text-[3.7vw] w-[50%] mb-[1vw]">
                            {`${TranslateFunction(`sub_title_withcart_item`)}`}
                        </span>
                    )}
                    {userCart.cart.length < 1 && (
                        <span className="text-[2.3vw] w-[50%] mb-[1vw]">
                            {`${TranslateFunction(`sub_title`)}`}
                        </span>
                    )}
                </div>
                <div ref={ref} className="shrink h-[42vw] grow overflow-y-auto overflow-x-hidden">
                    <MenuRow
                        focusKey="MENU-ROW"
                        title={`${TranslateFunction(`menu_row_title`)}`}
                        onFocus={onRowFocus}
                    />
                    <PastOrdersRow
                        focusKey="PAST-ORDERS-ROW"
                        title={`${TranslateFunction(`past_orders_row_title`)}`}
                        onFocus={onRowFocus}
                    />
                </div>
            </div>
        </FocusContext.Provider>
    );
};
